import { Component, type ReactNode } from 'react'
import { useI18n } from '../i18n'

interface Props { children: ReactNode }
interface State { error: Error | null }

/**
 * Catches render/commit errors in the routed page so a single bad component
 * shows a recoverable screen instead of a blank app that needs a hard refresh.
 * The Navbar/Footer live outside this boundary and stay usable; navigating to
 * another route remounts it (see the key={pathname} in App), so a click on the
 * nav recovers without a reload.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: { componentStack?: string }) {
    // Readable in production once source maps ship (see vite.config.ts).
    console.error('[HorseSheli] render error:', error, info?.componentStack)
  }

  render() {
    if (this.state.error) {
      return <CrashScreen onRetry={() => this.setState({ error: null })} />
    }
    return this.props.children
  }
}

function CrashScreen({ onRetry }: { onRetry: () => void }) {
  const { t } = useI18n()
  return (
    <main className="mx-auto flex min-h-[60vh] max-w-lg flex-col items-center justify-center px-5 py-16 text-center">
      <div className="text-5xl">🐴</div>
      <h1 className="mt-4 text-2xl font-extrabold tracking-tight">{t('crash.title')}</h1>
      <p className="mt-2 text-brand-600 dark:text-brand-300">{t('crash.body')}</p>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <button onClick={onRetry} className="btn-ghost">{t('crash.retry')}</button>
        <button onClick={() => window.location.reload()} className="btn-primary">{t('crash.reload')}</button>
      </div>
    </main>
  )
}
